import React from 'react';
import { RegionCode, getRegion } from '../utils/region';
// Brand mark used site-wide (navbar, mobile menu). Each domain ships TWO
// finished artworks — a black wordmark for light surfaces and a white wordmark
// for dark surfaces. We swap between them with Tailwind's `dark:` variant
// rather than filtering a single file, so the artwork always renders exactly as
// exported.
const LOGO_BY_REGION: Record<RegionCode, {
  light: string;
  dark: string;
  /**
   * Scrolled-navbar artwork: the green "206" glyph on its own, no wordmark.
   * Falls back to the dark wordmark for regions without a dedicated export.
   */
  scroll?: string;
}> = {
  '253': {
    light: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/npQqZiQ1q8dULzSaV3t1Rn/253code.png'
  },
  '206': {
    // Single white wordmark. The light-surface copy is rendered through
    // `brightness-0` (see below), which flips the white artwork to solid black
    // without needing a second export.
    light: 'https://cdn.magicpatterns.com/uploads/vqULqrPYCQphXVG1Phq2vm/206_new_logo.png',
    dark: 'https://cdn.magicpatterns.com/uploads/vqULqrPYCQphXVG1Phq2vm/206_new_logo.png',
    scroll: 'https://cdn.magicpatterns.com/uploads/5k3ubncfAmE3vfAoysrVLA/206_new_logo_scroll.png'
  },
  '425': {
    light: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png',
    dark: 'https://cdn.magicpatterns.com/uploads/kmHPuLicBRxog1QSdiQDmA/425code.png'
  }
};
/**
 * Resolve the active domain's logo image URL. Defaults to the dark-surface
 * (white) artwork, which is what the existing inline navbar `<img>` call sites
 * expect.
 */
export function getLogoSrc(region?: RegionCode, surface: 'light' | 'dark' | 'scroll' = 'dark'): string {
  const entry = LOGO_BY_REGION[region ?? getRegion()];
  if (surface === 'scroll') return entry.scroll ?? entry.dark;
  return entry[surface];
}
// TEMPORARY GLOBAL HIDE — every logo across the site is suppressed
// while this flag is true (the Logo component below, plus the inline
// <img> logos in Navbar.tsx that import this constant). To restore the
// logos, flip this back to `false` and re-publish.
export const LOGOS_HIDDEN = false;
interface LogoProps {
  className?: string;
  imgClassName?: string;
  /**
   * Retained for backwards-compatibility. Each theme now has its own finished
   * artwork, so nothing is inverted at runtime. Pass `false` to pin the
   * white-on-dark artwork regardless of theme (e.g. an always-dark surface).
   */
  invertOnLight?: boolean;
  /**
   * Retained for backwards-compatibility with existing call sites. The logo is
   * now a single static brand mark, so these no longer affect rendering.
   */
  cycleEvery?: number;
  region?: RegionCode;
  pinned?: boolean;
  /**
   * When true, a colored silhouette of the logo crossfades in over the base
   * mark. Used by the navbar to tint the logo forest-green once it collapses
   * on scroll.
   */
  tinted?: boolean;
  /** Color the tint overlay fills the logo with. Defaults to forest green. */
  tintColor?: string;
}
/**
 * 206Gravel brand mark — theme-aware finished artwork (black on light surfaces,
 * white on dark surfaces).
 */
export function Logo({
  className = '',
  imgClassName = 'h-10 w-auto',
  invertOnLight = true,
  tinted = false,
  tintColor = '#1E7A3C'
}: LogoProps) {
  // While LOGOS_HIDDEN is true, every Logo instance renders nothing.
  // Flip the constant above to `false` (or remove this block) to restore.
  if (LOGOS_HIDDEN) return null;
  const region = getRegion();
  const lightSrc = getLogoSrc(region, 'light');
  const darkSrc = getLogoSrc(region, 'dark');
  const scrollSrc = getLogoSrc(region, 'scroll');
  const baseOpacity = tinted ? 0 : 1;
  return <span className={`relative inline-block ${className}`} data-id="element-1159" data-mp="2yoq7ii1w57z5">
      {/* Light surfaces — the white artwork inverted to solid black via
          `brightness-0`. Suppressed entirely when `invertOnLight` is false,
          which pins the white artwork for call sites that always sit on a dark
          surface. */}
      {invertOnLight && <img src={lightSrc} alt="206Gravel" className={`${imgClassName} block brightness-0 dark:hidden pointer-events-none transition-opacity duration-1000`} style={{
      opacity: baseOpacity
    }} data-id="element-1160" data-mp="q8ytm5ont1ob" />}
      {/* White wordmark — dark surfaces (and any always-dark call site). */}
      <img src={darkSrc} alt={invertOnLight ? '' : '206Gravel'} aria-hidden={invertOnLight ? true : undefined} className={`${imgClassName} ${invertOnLight ? 'hidden dark:block' : 'block'} pointer-events-none transition-opacity duration-1000`} style={{
      opacity: baseOpacity
    }} data-id="element-1161" data-mp="1476qkdlp42zh" />
      {/* Scrolled state — the green "206" glyph alone, crossfaded in over the
          full wordmark. Same artwork in both themes, since the green reads on
          light and dark surfaces alike. */}
      <img src={scrollSrc} alt="" aria-hidden="true" className={`${imgClassName} absolute inset-0 block pointer-events-none transition-opacity duration-1000`} style={{
      opacity: tinted ? 1 : 0,
      objectFit: 'contain',
      objectPosition: 'left center'
    }} data-id="element-1162" data-mp="3kw2aveegbbqb" />
    </span>;
}